import { BackendDevice } from 'models'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import Constants from 'api-constants'
import { NagraTypes } from '../models/NagraTypes'

const DEVICE_ACTIVATION_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
}

const NAGRA_DEVICE_TYPE = {
    MANAGED: 'MANAGED',
    OPEN_DEVICE: 'OPEN',
}

const DEVICE_TYPE_MAPPING = {
    [NAGRA_DEVICE_TYPE.MANAGED]: BackendDevice.TYPE.STB_MANAGED,
    [NAGRA_DEVICE_TYPE.OPEN_DEVICE]: BackendDevice.TYPE.OPEN_DEVICE,
}

/**
 * Parse user devices
 * @param {Array<Object>} userDevices - array of user's devices
 * @returns {Array<BackendDevice>}
 */
export const userDeviceFactory = (userDevices = []) => {
    if (!userDevices.length) return []

    const { platform } = ConfigHelper.getInstance().getConfig()

    return userDevices
        .map((userDevice) => {
            const isActiveDevice = userDevice?.activationStatus === DEVICE_ACTIVATION_STATUS.ACTIVE
            if (isActiveDevice) {
                const isStbDevice = userDevice.deviceType === NAGRA_DEVICE_TYPE.MANAGED
                const isMyDevice = isUserPersonalDevice(userDevice, platform)

                let deviceName
                if (userDevice.name) {
                    deviceName = !isStbDevice ? userDevice.name : `STB - ${userDevice.name}`
                } else if (userDevice?.deviceInformation?.device?.hardware) {
                    const { model, manufacturer, type } =
                        userDevice.deviceInformation.device.hardware || {}
                    if (manufacturer === 'unknown' || model === 'unknown') {
                        deviceName = `${getDeviceNameByHeuristicApproach(type)} ${
                            userDevice.publicId
                        }`
                    } else if (manufacturer && model) {
                        if (model?.toLowerCase?.()?.includes(manufacturer?.toLowerCase?.())) {
                            deviceName = `${model}`
                        } else {
                            deviceName = `${manufacturer} ${model}`
                        }
                    } else if (model) {
                        deviceName = model
                    } else if (manufacturer) {
                        deviceName = `${manufacturer} ${userDevice.publicId}`
                    } else {
                        deviceName = `${getDeviceNameByHeuristicApproach(type)} ${
                            userDevice.publicId
                        }`
                    }
                } else if (userDevice.publicId) {
                    deviceName = `Device ${userDevice.publicId}`
                }

                return new BackendDevice({
                    id: userDevice._id,
                    publicId: userDevice.publicId,
                    name: deviceName || 'unknown',
                    deviceUUID: getDeviceIdByType(userDevice),
                    active: isActiveDevice,
                    deviceType: DEVICE_TYPE_MAPPING[userDevice.deviceType],
                    deletable: !isStbDevice && !isMyDevice,
                    displayable: !isStbDevice,
                    deviceInformation: userDevice?.deviceInformation,
                })
            }
            return null
        })
        .filter(Boolean)
}

/**
 * Returns if a device belong to the user or not
 * @param {Object} deviceInfo - object of user's device
 * @returns {Boolean} True if it is user device or not
 */
const isUserPersonalDevice = (deviceInfo = {}, platform) => {
    if (platform === Constants.platform.androidTvStb) {
        if (deviceInfo.deviceType === NAGRA_DEVICE_TYPE.MANAGED) {
            return deviceInfo.caSN === DataHelper.getInstance().getData(DataHelper.STORE_KEY.CASN)
        }
    } else {
        /* On nagra platform, there is no device ID set by the device, at every FIRST login nagra generates a client ID
           And this client ID corresponds to the device
        */
        return (
            deviceInfo.publicId ===
            DataHelper.getInstance().getData(DataHelper.STORE_KEY.SECONDARY_CLIENT_ID)
        )
    }
}

/**
 * Returns a proper device id according to the device type
 * @param {Object} deviceInfo - object of user's device
 * @returns {String} returns deviceInfo.caSN in case of deviceType is 'MANAGED'
 * or returns deviceInfo.publicId in case of deviceType is 'OPEN'
 */
const getDeviceIdByType = (deviceInfo) => {
    switch (deviceInfo.deviceType) {
        case NAGRA_DEVICE_TYPE.MANAGED:
            return deviceInfo.caSN
        case NAGRA_DEVICE_TYPE.OPEN_DEVICE:
            return deviceInfo.publicId
        default:
            return deviceInfo.publicId
    }
}

/**
 * Returns a proper device id according to the device type
 * @param {Object} deviceInfo - object of user's device
 * @returns {String} returns deviceInfo.caSN in case of deviceType is 'MANAGED'
 * or returns deviceInfo.publicId in case of deviceType is 'OPEN'
 */
const getDeviceNameByHeuristicApproach = (type = '') => {
    switch (type) {
        /*
            Handle aliases from history
            */
        case 'Mobile':
        case 'IFSMobile':
        case 'mobile':
        case NagraTypes.HARDWARE_TYPE.MOBILE:
        case `IFS${NagraTypes.HARDWARE_TYPE.MOBILE}`:
            return 'Mobile'

        /*
            Handle aliases from history
            */
        case 'STB':
        case 'IFSSTB':
        case 'Stb':
        case 'stb':
        case 'ATV':
        case 'Android TV':
        case NagraTypes.HARDWARE_TYPE.STB:
        case `IFS${NagraTypes.HARDWARE_TYPE.STB}`:
            return 'Android TV'

        case NagraTypes.HARDWARE_TYPE.AMAZON_TV:
        case `IFS${NagraTypes.HARDWARE_TYPE.AMAZON_TV}`:
            return 'Amazon TV'

        case 'IFSBrowser':
        case 'Browser':
        case NagraTypes.HARDWARE_TYPE.BROWSER:
        case `IFS${NagraTypes.HARDWARE_TYPE.BROWSER}`:
            return 'Browser'

        case NagraTypes.HARDWARE_TYPE.APPLE_TV:
        case `IFS${NagraTypes.HARDWARE_TYPE.APPLE_TV}`:
            return 'Apple TV'

        case NagraTypes.HARDWARE_TYPE.SMART_TV:
        case `IFS${NagraTypes.HARDWARE_TYPE.SMART_TV}`:
            return 'Smart TV'

        default:
            return type?.includes('IFS') ? type.replace('IFS', '') : type
    }
}
